import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/shadcn/dialog';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/shadcn/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/shadcn/collapsible';
import { useQueryClient } from 'react-query';
import {
  QUERY_KEYS,
  useCreditWalletSummary,
  usePostAdminFinanceCorrection,
  useUsdWalletSummary,
} from '../../hooks/useApi';
import { formatCurrency } from '../../utils';
import type { AdminFinanceCorrectionOwnerType } from '../../types';

export interface FinanceCorrectionDebitModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ownerType: AdminFinanceCorrectionOwnerType;
  ownerId: string;
  onApplied?: () => void;
}

function RequiredMark() {
  return (
    <span className="text-destructive" aria-hidden="true">
      *
    </span>
  );
}

const FinanceCorrectionDebitModal: React.FC<FinanceCorrectionDebitModalProps> = ({
  open,
  onOpenChange,
  ownerType,
  ownerId,
  onApplied,
}) => {
  const mutation = usePostAdminFinanceCorrection();
  const queryClient = useQueryClient();
  const [walletKind, setWalletKind] = useState<'cdn_credit' | 'usd'>('cdn_credit');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [ticketId, setTicketId] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [warningOpen, setWarningOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setWalletKind('cdn_credit');
    setAmount('');
    setReason('');
    setTicketId('');
    setWarningOpen(false);
    setIdempotencyKey(
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `idem-${Date.now()}`,
    );
  }, [open]);

  const isPlayerOwner = ownerType === 'player';
  const effectiveWalletKind: 'cdn_credit' | 'usd' = isPlayerOwner ? 'cdn_credit' : walletKind;

  const ownerIdTrim = ownerId.trim();
  const summaryEnabled = open && ownerIdTrim.length > 0;

  const creditSummaryQuery = useCreditWalletSummary(
    { ownerType, ownerId: ownerIdTrim },
    summaryEnabled,
  );
  const usdSummaryQuery = useUsdWalletSummary(
    { ownerType, ownerId: ownerIdTrim, currency: 'USD' },
    summaryEnabled && !isPlayerOwner,
  );

  const availableCredits = useMemo(() => {
    if (!creditSummaryQuery.data?.walletId) return null;
    return (
      Number(creditSummaryQuery.data.balance ?? 0) - Number(creditSummaryQuery.data.reservedBalance ?? 0)
    );
  }, [creditSummaryQuery.data]);

  const ownerLabel = useMemo(() => {
    if (ownerType === 'shop') return 'shop';
    if (ownerType === 'agent') return 'agent';
    if (ownerType === 'player') return 'player';
    return 'super agent';
  }, [ownerType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amt = amount.trim() === '' ? NaN : Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Enter a positive amount.');
      return;
    }
    if (ticketId.trim().length < 3) {
      toast.error('Ticket id must be at least 3 characters.');
      return;
    }
    if (reason.trim().length < 10) {
      toast.error('Reason must be at least 10 characters.');
      return;
    }
    if (!idempotencyKey.trim()) {
      toast.error('Missing idempotency key; close and reopen the dialog.');
      return;
    }
    if (isPlayerOwner && availableCredits != null && amt > availableCredits) {
      toast.error('Debit amount cannot exceed the player available balance.');
      return;
    }

    try {
      const res = await mutation.mutateAsync({
        ownerType,
        ownerId,
        walletKind: effectiveWalletKind,
        direction: 'debit',
        amount: amt,
        reason: reason.trim(),
        ticketId: ticketId.trim(),
        idempotencyKey: idempotencyKey.trim(),
      });
      if (res.data?.idempotent) {
        toast.success('Request already applied (idempotent). Balances unchanged.');
      } else {
        toast.success('Balance correction applied.');
      }
      void queryClient.invalidateQueries(['creditWalletSummary']);
      void queryClient.invalidateQueries(['usdWalletSummary']);
      if (isPlayerOwner) {
        void queryClient.invalidateQueries([QUERY_KEYS.WALLET, ownerIdTrim]);
        void queryClient.invalidateQueries([QUERY_KEYS.USER_DETAILS, ownerIdTrim]);
        void queryClient.invalidateQueries([QUERY_KEYS.WALLET_TRANSACTIONS]);
      }
      onApplied?.();
      onOpenChange(false);
    } catch {
      // toast from mutation onError
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90vh,100dvh)] max-w-md flex-col gap-0 overflow-hidden p-0">
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-6">
        <DialogHeader>
          <DialogTitle className="flex items-start gap-2 pr-6 text-left">
            <AlertTriangle
              className="h-5 w-5 shrink-0 text-amber-600 mt-0.5"
              aria-hidden
            />
            <span>Balance Correction — Debit</span>
          </DialogTitle>
          <DialogDescription asChild>
            <div className="text-left text-sm text-muted-foreground space-y-3 pt-1">
              <p>
                <strong className="text-foreground">Super admin / system admin only.</strong> This corrects the ledger
                / claws back funds that were already posted (bugs, duplicate callbacks, approved operational actions with
                a ticket). It is <strong className="text-foreground">not</strong> an external cash-out or partner payout.
                {isPlayerOwner ? (
                  <>
                    {' '}
                    For players, credits are <strong className="text-foreground">recalled to the platform</strong> (not
                    returned to the shop wallet like shop redeem).
                  </>
                ) : null}
              </p>
              <Collapsible open={warningOpen} onOpenChange={setWarningOpen}>
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-md border border-amber-500/40 bg-amber-50/80 px-3 py-2.5 text-left text-sm font-medium text-amber-950 transition-colors hover:bg-amber-100/90 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-50 dark:hover:bg-amber-950/60"
                    aria-expanded={warningOpen}
                  >
                    <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                    <span className="min-w-0 flex-1">What this action does (read before applying)</span>
                    {warningOpen ? (
                      <ChevronDown className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                    )}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="overflow-hidden">
                  <div
                    role="region"
                    aria-label="Warning details"
                    className="mt-2 rounded-md border border-amber-500/40 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-500/30 px-3 py-2.5 text-amber-950 dark:text-amber-100"
                  >
                    <ul className="list-disc space-y-2 pl-5 text-sm font-normal leading-relaxed">
                      <li>
                        Immediately debits the{' '}
                        <strong className="font-semibold">
                          {effectiveWalletKind === 'usd' ? 'USD wallet balance' : 'CDN credits'}
                        </strong>{' '}
                        for this <strong className="font-semibold">{ownerLabel}</strong> by the amount you enter, and
                        posts an adjustment / debit on the ledger (ticket + reason are stored for audit).
                        {isPlayerOwner ? (
                          <>
                            {' '}
                            Only the <strong className="font-semibold">available</strong> portion (total minus reserved)
                            may be debited.
                          </>
                        ) : null}
                      </li>
                      {isPlayerOwner ? (
                        <li>
                          This is <strong className="font-semibold">not</strong> shop redeem: the player&apos;s shop
                          wallet does <strong className="font-semibold">not</strong> receive these credits. Use for hack
                          / loss prevention with an incident ticket.
                        </li>
                      ) : null}
                      <li>
                        The balance drops for real; there is no automatic undo from this screen. Verify the{' '}
                        {!isPlayerOwner ? (
                          <>
                            <strong className="font-semibold">wallet type</strong> (CDN vs USD) and{' '}
                          </>
                        ) : null}
                        <strong className="font-semibold">amount</strong> before you confirm.
                      </li>
                      <li>
                        The <strong className="font-semibold">idempotency key</strong> is generated each time you open
                        this dialog (it is <strong className="font-semibold">not</strong> the ticket field). If the same
                        Apply request is sent twice (double-click, flaky network retry with the same key), the server{' '}
                        <strong className="font-semibold">only debits once</strong>. The ticket / incident id is for
                        operations audit and is separate from that key.
                      </li>
                      <li>
                        Closing and reopening the dialog issues a <strong className="font-semibold">new key</strong> — a
                        new operation. The same ticket with a new key can still result in another debit if you submit
                        again.
                      </li>
                    </ul>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid gap-4 py-2">
          <div className="grid gap-2">
            {isPlayerOwner ? (
              <Label>CDN credits (player wallet)</Label>
            ) : (
              <>
                <Label>
                  Wallet <RequiredMark />
                </Label>
                <Select value={walletKind} onValueChange={(v) => setWalletKind(v as 'cdn_credit' | 'usd')}>
                  <SelectTrigger>
                    <SelectValue placeholder="Wallet type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cdn_credit">CDN credits</SelectItem>
                    <SelectItem value="usd">USD wallet</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
            {summaryEnabled ? (
              <div
                className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-sm"
                aria-live="polite"
              >
                {effectiveWalletKind === 'cdn_credit' ? (
                  creditSummaryQuery.isLoading ? (
                    <p className="text-muted-foreground">Loading CDN credit balance…</p>
                  ) : creditSummaryQuery.isError ? (
                    <p className="text-destructive">Could not load CDN credit balance.</p>
                  ) : !creditSummaryQuery.data?.walletId ? (
                    <p className="text-muted-foreground">No CDN credit wallet for this owner yet.</p>
                  ) : (
                    <div className="flex justify-between gap-2 font-mono text-sm tabular-nums">
                      <span className="text-muted-foreground">Available (CDN credits)</span>
                      <span className="font-medium">
                        {formatCurrency(
                          Number(creditSummaryQuery.data.balance ?? 0) -
                            Number(creditSummaryQuery.data.reservedBalance ?? 0),
                        )}
                      </span>
                    </div>
                  )
                ) : usdSummaryQuery.isLoading ? (
                  <p className="text-muted-foreground">Loading USD balance…</p>
                ) : usdSummaryQuery.isError ? (
                  <p className="text-destructive">Could not load USD balance.</p>
                ) : !usdSummaryQuery.data?.walletId ? (
                  <p className="text-muted-foreground">No USD online wallet for this owner yet.</p>
                ) : (
                  <div className="flex justify-between gap-2 font-mono text-sm tabular-nums">
                    <span className="text-muted-foreground">Available (USD online)</span>
                    <span className="font-medium">
                      {formatCurrency(Number(usdSummaryQuery.data.balanceNet ?? 0))}
                    </span>
                  </div>
                )}
              </div>
            ) : null}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="fc-amount">
              Amount <RequiredMark />
            </Label>
            <Input
              id="fc-amount"
              type="number"
              step="any"
              min={0}
              inputMode="decimal"
              value={amount}
              onChange={(ev) => setAmount(ev.target.value)}
              disabled={mutation.isLoading}
              placeholder="e.g. 100.50"
            />
            {isPlayerOwner && availableCredits != null && amount.trim() !== '' ? (
              (() => {
                const amtPreview = Number(amount);
                if (!Number.isFinite(amtPreview) || amtPreview <= 0) return null;
                if (amtPreview > availableCredits) {
                  return (
                    <p className="text-xs text-destructive">
                      Amount exceeds available balance ({formatCurrency(availableCredits)}).
                    </p>
                  );
                }
                return null;
              })()
            ) : null}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="fc-ticket">
              Ticket / incident id <RequiredMark />
            </Label>
            <Input
              id="fc-ticket"
              value={ticketId}
              onChange={(ev) => setTicketId(ev.target.value)}
              disabled={mutation.isLoading}
              placeholder="INC-12345"
              autoComplete="off"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="fc-reason">
              Reason (min 10 characters) <RequiredMark />
            </Label>
            <textarea
              id="fc-reason"
              className="flex min-h-[88px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              value={reason}
              onChange={(ev) => setReason(ev.target.value)}
              disabled={mutation.isLoading}
              placeholder="Describe the bug, duplicate callback, or approved clawback."
            />
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              {mutation.isLoading ? 'Applying…' : 'Apply debit'}
            </Button>
          </DialogFooter>
        </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FinanceCorrectionDebitModal;
